import { useState, useCallback, useEffect, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import * as THREE from "three";
import { useProject } from "./ProjectContext";
import { TerrainMesh3D, elevationColor, type TerrainData } from "./TerrainMesh3D";
import OlMap from "ol/Map";
import View from "ol/View";
import ImageLayer from "ol/layer/Image";
import ImageWMS from "ol/source/ImageWMS";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import { Polygon } from "ol/geom";
import { Stroke, Fill, Style } from "ol/style";
import { createBaseLayers } from "./mapUtils";
import proj4 from "proj4";
import "ol/ol.css";

/** After panning, raycast from camera through screen center onto terrain and move orbit target there.
 *  Shows a small marker at the pivot point. */
function OrbitPivotSnap() {
  const { controls, camera, scene, gl } = useThree();
  const raycaster = useRef(new THREE.Raycaster());
  const screenCenter = useRef(new THREE.Vector2(0, 0));
  const markerRef = useRef<THREE.Mesh>(null);
  const isPanning = useRef(false);
  const needsSnap = useRef(false);

  useEffect(() => {
    const canvas = gl.domElement;
    const onMouseDown = (e: MouseEvent) => {
      // Right-click (button 2) or middle-click (button 1) = pan
      if (e.button === 2 || e.button === 1) isPanning.current = true;
    };
    const onMouseUp = (e: MouseEvent) => {
      if (e.button === 2 || e.button === 1) {
        if (isPanning.current) needsSnap.current = true;
        isPanning.current = false;
      }
    };
    canvas.addEventListener("mousedown", onMouseDown);
    canvas.addEventListener("mouseup", onMouseUp);
    return () => {
      canvas.removeEventListener("mousedown", onMouseDown);
      canvas.removeEventListener("mouseup", onMouseUp);
    };
  }, [gl]);

  useFrame(() => {
    if (!controls || !(controls as any).target) return;
    const target = (controls as any).target as THREE.Vector3;

    if (markerRef.current) {
      markerRef.current.position.copy(target);
    }

    if (!needsSnap.current) return;
    needsSnap.current = false;

    raycaster.current.setFromCamera(screenCenter.current, camera);
    const hits = raycaster.current.intersectObjects(scene.children, true);
    const hit = hits.find(h => h.object.type === "Mesh" && h.object.geometry?.attributes?.elevation);
    if (hit) {
      target.copy(hit.point);
      (controls as any).update();
    }
  });

  return (
    <mesh ref={markerRef}>
      <octahedronGeometry args={[2, 0]} />
      <meshBasicMaterial color="#ffffff" wireframe opacity={0.4} transparent />
    </mesh>
  );
}

const PANEL_BG = "rgba(0,0,0,0.82)";
const PANEL_BORDER = "rgba(255,255,255,0.12)";

/** 3D compass arrows and scale bar placed at the corner of the terrain.
 *  Coordinate system: X = east, Y = north, Z = up (standard cartesian).
 */
function SceneOverlays({ terrain, originOffset }: {
  terrain: TerrainData;
  originOffset: { x: number; y: number; z: number };
}) {
  const ext = terrain.extent;
  // Place at SW corner of terrain (min easting, min northing, min elevation)
  const cornerX = ext.minEasting - originOffset.x;
  const cornerY = ext.minNorthing - originOffset.y;
  const cornerZ = (terrain.stats?.min ?? 0) - originOffset.z - 5;

  // Arrow length = nice round scale value
  const terrainWidth = ext.maxEasting - ext.minEasting;
  const terrainHeight = ext.maxNorthing - ext.minNorthing;
  const niceValues = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000];
  const rawScale = Math.min(terrainWidth, terrainHeight) * 0.2;
  let arrowLen = niceValues[0];
  for (const v of niceValues) {
    if (v <= rawScale) arrowLen = v;
    else break;
  }
  const scaleLabel = arrowLen >= 1000 ? `${arrowLen / 1000} km` : `${arrowLen} m`;
  const shaftRadius = arrowLen * 0.015;
  const headRadius = arrowLen * 0.04;
  const headLen = arrowLen * 0.12;

  return (
    <group position={[cornerX, cornerY, cornerZ]}>
      {/* East arrow: +X */}
      <group>
        <mesh position={[arrowLen / 2, 0, 0]} rotation={[0, 0, -Math.PI / 2]}>
          <cylinderGeometry args={[shaftRadius, shaftRadius, arrowLen, 8]} />
          <meshStandardMaterial color="#4fc3f7" />
        </mesh>
        <mesh position={[arrowLen, 0, 0]} rotation={[0, 0, -Math.PI / 2]}>
          <coneGeometry args={[headRadius, headLen, 8]} />
          <meshStandardMaterial color="#4fc3f7" />
        </mesh>
        <Html position={[arrowLen + headLen + 2, 0, 0]} center style={{ pointerEvents: "none", userSelect: "none" }}>
          <span style={{ color: "#4fc3f7", fontSize: 11, fontWeight: 700, textShadow: "0 0 4px rgba(0,0,0,0.8)" }}>E</span>
        </Html>
      </group>

      {/* North arrow: +Y */}
      <group>
        <mesh position={[0, arrowLen / 2, 0]}>
          <cylinderGeometry args={[shaftRadius, shaftRadius, arrowLen, 8]} />
          <meshStandardMaterial color="#e53935" />
        </mesh>
        <mesh position={[0, arrowLen, 0]}>
          <coneGeometry args={[headRadius, headLen, 8]} />
          <meshStandardMaterial color="#e53935" />
        </mesh>
        <Html position={[0, arrowLen + headLen + 2, 0]} center style={{ pointerEvents: "none", userSelect: "none" }}>
          <span style={{ color: "#e53935", fontSize: 11, fontWeight: 700, textShadow: "0 0 4px rgba(0,0,0,0.8)" }}>N</span>
        </Html>
      </group>

      {/* Up arrow: +Z */}
      <group>
        <mesh position={[0, 0, arrowLen * 0.4]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[shaftRadius, shaftRadius, arrowLen * 0.8, 8]} />
          <meshStandardMaterial color="#66bb6a" />
        </mesh>
        <mesh position={[0, 0, arrowLen * 0.8]} rotation={[Math.PI / 2, 0, 0]}>
          <coneGeometry args={[headRadius, headLen, 8]} />
          <meshStandardMaterial color="#66bb6a" />
        </mesh>
      </group>

      {/* Scale label on the east arrow */}
      <Html position={[arrowLen * 0.5, 0, -headRadius * 4]} center style={{ pointerEvents: "none", userSelect: "none" }}>
        <span style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, fontWeight: 600, textShadow: "0 0 4px rgba(0,0,0,0.9)", whiteSpace: "nowrap" }}>{scaleLabel}</span>
      </Html>
    </group>
  );
}

interface WorkflowStep {
  id: string;
  number: number;
  title: string;
  description: string;
  status: "available" | "coming-soon" | "active" | "done";
  details: string[];
}

const WORKFLOW_STEPS: WorkflowStep[] = [
  {
    id: "terrain",
    number: 1,
    title: "Terrain model",
    description: "Import or fetch a terrain surface mesh as the foundation for the geological model.",
    status: "available",
    details: [
      "Fetch DTM from Kartverket (10m or 1m grid)",
      "Upload GeoTIFF, LandXML, or OBJ/PLY",
      "Terrain mesh is used as the top surface for all geological layers",
    ],
  },
  {
    id: "gis-layers",
    number: 2,
    title: "GIS layers",
    description: "Import and manage map images to drape on the terrain surface.",
    status: "available",
    details: [
      "Import from WMS — browse NGU bedrock maps on a 2D map and save the terrain extent as an image",
      "Upload custom map images (PNG / JPEG)",
      "Saved images are stored on the server for offline use",
      "Toggle layers on/off as terrain overlays in the 3D view",
    ],
  },
  {
    id: "topo-order",
    number: 3,
    title: "Geological topology",
    description: "Define the chronological order and interaction type of geological units.",
    status: "coming-soon",
    details: [
      "List geological units from the map or define manually",
      "Set chronological order (oldest to youngest)",
      "Assign interaction type per unit:",
      "  • Depositional — layer deposited on top of older surfaces",
      "  • Erosional — unconformity that truncates everything below",
      "  • Intrusive — body that cuts through existing units",
      "Topology determines how surfaces interact when building volumes",
    ],
  },
  {
    id: "surface-contacts",
    number: 4,
    title: "Surface contacts & structures",
    description: "Draw geological contacts and structural features on the terrain surface.",
    status: "coming-soon",
    details: [
      "Draw contact lines on the 3D terrain mesh or on the 2D map",
      "Lines define boundaries between geological units at the surface",
      "Add structural measurements (strike/dip) as orientation constraints",
      "Contact points become RBF control points for surface fitting",
    ],
  },
  {
    id: "cross-sections",
    number: 5,
    title: "Cross sections",
    description: "Define vertical cross-section planes and sketch subsurface geology.",
    status: "coming-soon",
    details: [
      "Place section lines on the map (click start and end points)",
      "Section plane shows terrain profile and any existing borehole data",
      "Draw estimated geological boundaries on each section",
      "Lines represent top or bottom of a specific layer",
      "Multiple sections constrain the 3D interpolation between them",
    ],
  },
  {
    id: "section-drawing",
    number: 6,
    title: "Section interpretation",
    description: "Draw and refine layer boundaries on cross sections with real-time feedback.",
    status: "coming-soon",
    details: [
      "Draw polylines on the section for each geological contact",
      "Assign each line to a geological unit boundary (top/bottom)",
      "Real-time polygon fill shows interpreted geology in the section plane",
      "Lines are simplified to control points for RBF surface fitting",
      "Off-surface points generated automatically for signed distance field",
    ],
  },
  {
    id: "volumes",
    number: 7,
    title: "Build & refine volumes",
    description: "Compute 3D geological volumes and iterate on the model.",
    status: "coming-soon",
    details: [
      "RBF implicit surfaces fitted from all control points per unit boundary",
      "Surfaces evaluated in topological order with interaction rules",
      "3D volume mesh generated via marching cubes or similar",
      "Movable 3D slice plane for inspecting the model (reuses ClipPlane3D)",
      "Iterate: adjust contacts, sections, topology, and rebuild",
    ],
  },
];

export function GeoModelView({ onBack, onNavigate }: { onBack: () => void; onNavigate?: (route: string) => void }) {
  const project = useProject();
  const [activeStep, setActiveStep] = useState<string>("terrain");
  const [terrains, setTerrains] = useState<string[]>([]);
  const [activeTerrain, setActiveTerrain] = useState<TerrainData | null>(null);
  const [loadingTerrain, setLoadingTerrain] = useState(false);
  const [showContours, setShowContours] = useState(true);
  const [contourInterval, setContourInterval] = useState(1);
  const [showElevationColors, setShowElevationColors] = useState(false);
  const [showHillshade, setShowHillshade] = useState(true);
  // GIS layers
  const [gisLayers, setGisLayers] = useState<{ id: string; label: string; wmsLayers: string; bbox: string; width: number; height: number; createdAt: string }[]>([]);
  const [activeGisLayerId, setActiveGisLayerId] = useState<string | null>(null);
  const [gisOverlayTexture, setGisOverlayTexture] = useState<THREE.Texture | null>(null);
  const gisOverlayTextureRef = useRef<THREE.Texture | null>(null);
  const [showWmsImport, setShowWmsImport] = useState(false);

  // Fetch list of available terrains
  useEffect(() => {
    fetch("/api/terrain/list")
      .then((r) => r.json())
      .then((data) => {
        if (data.terrains?.length) {
          setTerrains(data.terrains);
        }
      })
      .catch(() => {});
  }, []);

  // Fetch saved GIS layers
  const refreshGisLayers = useCallback(() => {
    fetch("/api/geo-layers/list")
      .then((r) => r.json())
      .then((data) => { if (data.layers) setGisLayers(data.layers); })
      .catch(() => {});
  }, []);

  useEffect(() => { refreshGisLayers(); }, [refreshGisLayers]);

  // Load active GIS layer as texture
  useEffect(() => {
    if (!activeGisLayerId) {
      setGisOverlayTexture(null);
      return;
    }
    let cancelled = false;
    fetch(`/api/geo-layers/${activeGisLayerId}/image`)
      .then((r) => { if (!r.ok) throw new Error("Failed"); return r.blob(); })
      .then((blob) => createImageBitmap(blob))
      .then((bitmap) => {
        if (cancelled) return;
        const tex = new THREE.Texture(bitmap as any);
        tex.needsUpdate = true;
        tex.minFilter = THREE.LinearFilter;
        tex.magFilter = THREE.LinearFilter;
        tex.flipY = false;
        if (gisOverlayTextureRef.current) gisOverlayTextureRef.current.dispose();
        gisOverlayTextureRef.current = tex;
        setGisOverlayTexture(tex);
      })
      .catch((err) => { if (!cancelled) console.error("Failed to load GIS layer image:", err); });
    return () => { cancelled = true; };
  }, [activeGisLayerId]);

  const loadTerrain = useCallback(async (name: string) => {
    setLoadingTerrain(true);
    try {
      const resp = await fetch(`/api/terrain/${name}`);
      const data = await resp.json();
      setActiveTerrain(data);
    } catch (err) {
      console.error("Failed to load terrain:", err);
    } finally {
      setLoadingTerrain(false);
    }
  }, []);

  const deleteTerrain = useCallback(async (name: string) => {
    try {
      await fetch(`/api/terrain/${name}`, { method: "DELETE" });
      setTerrains((prev) => prev.filter((t) => t !== name));
      if (activeTerrain?.name === name) setActiveTerrain(null);
    } catch (err) {
      console.error("Failed to delete terrain:", err);
    }
  }, [activeTerrain]);

  const currentStep = WORKFLOW_STEPS.find((s) => s.id === activeStep);

  return (
    <div style={{
      height: "100dvh",
      display: "flex",
      flexDirection: "column",
      background: "#0a1628",
      color: "#f7f8fb",
      fontFamily: "system-ui, -apple-system, sans-serif",
    }}>
      {/* Header */}
      <header style={{
        flexShrink: 0,
        padding: "12px 20px",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 8,
            color: "#f7f8fb",
            cursor: "pointer",
            padding: "6px 12px",
            fontSize: "0.8rem",
            fontWeight: 600,
          }}
        >
          ← Back
        </button>
        <div>
          <div style={{ fontSize: "1rem", fontWeight: 700 }}>Geological Modeling</div>
          <div style={{ fontSize: "0.7rem", opacity: 0.45 }}>{project.name} — Build 3D geological model</div>
        </div>
      </header>

      {/* Body */}
      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        {/* Workflow sidebar */}
        <div style={{
          width: 260,
          flexShrink: 0,
          borderRight: "1px solid rgba(255,255,255,0.06)",
          overflowY: "auto",
          padding: "12px",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}>
          <div style={{
            fontSize: "0.68rem",
            fontWeight: 600,
            opacity: 0.4,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            padding: "4px 8px",
            marginBottom: 4,
          }}>
            Workflow
          </div>

          {WORKFLOW_STEPS.map((step) => {
            const isActive = step.id === activeStep;
            const isDone = step.status === "done";
            const isAvailable = step.status === "available" || step.status === "active";

            return (
              <div key={step.id}>
              <button
                type="button"
                onClick={() => setActiveStep(step.id)}
                style={{
                  display: "flex",
                  gap: 10,
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: isActive
                    ? "1px solid rgba(123, 31, 162, 0.5)"
                    : `1px solid ${PANEL_BORDER}`,
                  background: isActive
                    ? "rgba(123, 31, 162, 0.15)"
                    : "rgba(255,255,255,0.02)",
                  color: "#f7f8fb",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "all 0.15s",
                  opacity: isAvailable || isDone ? 1 : 0.5,
                }}
              >
                {/* Step number */}
                <div style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: isDone
                    ? "rgba(76, 175, 80, 0.3)"
                    : isActive
                    ? "rgba(123, 31, 162, 0.4)"
                    : "rgba(255,255,255,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  flexShrink: 0,
                  color: isDone ? "#81c784" : isActive ? "#ce93d8" : "rgba(255,255,255,0.5)",
                }}>
                  {isDone ? "✓" : step.number}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}>
                    {step.title}
                    {step.status === "coming-soon" && (
                      <span style={{
                        fontSize: "0.58rem",
                        padding: "1px 5px",
                        borderRadius: 4,
                        background: "rgba(255,255,255,0.08)",
                        color: "rgba(255,255,255,0.35)",
                        fontWeight: 500,
                      }}>
                        soon
                      </span>
                    )}
                  </div>
                  <div style={{
                    fontSize: "0.65rem",
                    opacity: 0.45,
                    marginTop: 2,
                    lineHeight: 1.3,
                  }}>
                    {step.description}
                  </div>
                </div>
              </button>
              {/* Terrain list nested under terrain step */}
              {isActive && step.id === "terrain" && (
                <div style={{
                  marginLeft: 38,
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  marginTop: 2,
                }}>
                  {terrains.map((name) => {
                    const isLoaded = activeTerrain?.name === name;
                    const label = name.replace(/^kartverket-/, "").replace(/-(\d+)$/, (_, ts: string) => {
                      const d = new Date(Number(ts));
                      return ` ${d.toLocaleDateString()}`;
                    });
                    return (
                      <div key={name} style={{ display: "flex", gap: 3 }}>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); loadTerrain(name); }}
                          disabled={loadingTerrain}
                          style={{
                            flex: 1,
                            padding: "4px 8px",
                            borderRadius: 6,
                            border: isLoaded
                              ? "1px solid rgba(76, 175, 80, 0.4)"
                              : `1px solid ${PANEL_BORDER}`,
                            background: isLoaded
                              ? "rgba(76, 175, 80, 0.1)"
                              : "rgba(255,255,255,0.02)",
                            color: "#f7f8fb",
                            cursor: loadingTerrain ? "wait" : "pointer",
                            textAlign: "left",
                            fontSize: "0.65rem",
                            fontWeight: 500,
                          }}
                        >
                          {isLoaded ? "✓ " : ""}{label}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); deleteTerrain(name); }}
                          style={{
                            padding: "4px 6px",
                            borderRadius: 6,
                            border: `1px solid ${PANEL_BORDER}`,
                            background: "rgba(255,255,255,0.02)",
                            color: "rgba(255,255,255,0.3)",
                            cursor: "pointer",
                            fontSize: "0.6rem",
                            lineHeight: 1,
                          }}
                          title="Delete terrain"
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                  {terrains.length === 0 && (
                    <div style={{ fontSize: "0.65rem", opacity: 0.4, padding: "2px 0" }}>
                      No terrain models yet
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onNavigate?.("terrain");
                    }}
                    style={{
                      padding: "4px 8px",
                      borderRadius: 6,
                      border: "1px solid rgba(255, 152, 0, 0.3)",
                      background: "rgba(255, 152, 0, 0.08)",
                      color: "rgba(255, 152, 0, 0.8)",
                      cursor: "pointer",
                      textAlign: "left",
                      fontSize: "0.65rem",
                      fontWeight: 600,
                    }}
                  >
                    + Import terrain...
                  </button>
                  {/* Display controls */}
                  {activeTerrain && (
                    <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontSize: "0.6rem", opacity: 0.6 }}>
                          <input
                            type="checkbox"
                            checked={showContours}
                            onChange={(e) => setShowContours(e.target.checked)}
                            style={{ margin: 0, accentColor: "#7b1fa2" }}
                          />
                          Contours
                        </label>
                        {showContours && (
                          <select
                            value={contourInterval}
                            onChange={(e) => setContourInterval(Number(e.target.value))}
                            style={{
                              background: "rgba(255,255,255,0.08)",
                              border: `1px solid ${PANEL_BORDER}`,
                              borderRadius: 4,
                              color: "#f7f8fb",
                              fontSize: "0.58rem",
                              padding: "1px 4px",
                              cursor: "pointer",
                            }}
                          >
                            <option value={1}>1 m</option>
                            <option value={2}>2 m</option>
                            <option value={5}>5 m</option>
                            <option value={10}>10 m</option>
                            <option value={20}>20 m</option>
                            <option value={50}>50 m</option>
                          </select>
                        )}
                      </div>
                      <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontSize: "0.6rem", opacity: 0.6 }}>
                        <input
                          type="checkbox"
                          checked={showHillshade}
                          onChange={(e) => setShowHillshade(e.target.checked)}
                          style={{ margin: 0, accentColor: "#7b1fa2" }}
                        />
                        Hillshade
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontSize: "0.6rem", opacity: 0.6 }}>
                        <input
                          type="checkbox"
                          checked={showElevationColors}
                          onChange={(e) => setShowElevationColors(e.target.checked)}
                          style={{ margin: 0, accentColor: "#7b1fa2" }}
                        />
                        Elevation colors
                      </label>
                      {showElevationColors && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 18 }}>
                          <span style={{ fontSize: "0.58rem", opacity: 0.5, fontFamily: "monospace", minWidth: 28, textAlign: "right" }}>
                            {activeTerrain.stats?.min ?? "?"}m
                          </span>
                          <div style={{
                            flex: 1,
                            height: 8,
                            borderRadius: 3,
                            background: `linear-gradient(to right, ${
                              Array.from({ length: 20 }, (_, i) => {
                                const t = i / 19;
                                const [r, g, b] = elevationColor(t);
                                return `rgb(${Math.round(r * 255)},${Math.round(g * 255)},${Math.round(b * 255)})`;
                              }).join(", ")
                            })`,
                            border: `1px solid ${PANEL_BORDER}`,
                          }} />
                          <span style={{ fontSize: "0.58rem", opacity: 0.5, fontFamily: "monospace", minWidth: 28 }}>
                            {activeTerrain.stats?.max ?? "?"}m
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {/* GIS layers controls nested under gis-layers step */}
              {isActive && step.id === "gis-layers" && activeTerrain && (
                <div style={{
                  marginLeft: 38,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                  marginTop: 2,
                }}>
                  <div style={{ fontSize: "0.58rem", fontWeight: 600, opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 }}>
                    Images
                  </div>
                  {gisLayers.length === 0 && (
                    <div style={{ fontSize: "0.62rem", opacity: 0.35, padding: "2px 0" }}>No saved layers yet</div>
                  )}
                  {gisLayers.map((layer) => {
                    const isActive = activeGisLayerId === layer.id;
                    return (
                      <div key={layer.id} style={{ display: "flex", gap: 3 }}>
                        <button
                          type="button"
                          onClick={() => setActiveGisLayerId(isActive ? null : layer.id)}
                          style={{
                            flex: 1,
                            padding: "4px 8px",
                            borderRadius: 6,
                            border: isActive
                              ? "1px solid rgba(76, 175, 80, 0.4)"
                              : `1px solid ${PANEL_BORDER}`,
                            background: isActive
                              ? "rgba(76, 175, 80, 0.1)"
                              : "rgba(255,255,255,0.02)",
                            color: "#f7f8fb",
                            cursor: "pointer",
                            textAlign: "left",
                            fontSize: "0.62rem",
                            fontWeight: 500,
                          }}
                        >
                          {isActive ? "✓ " : ""}{layer.label}
                        </button>
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            await fetch(`/api/geo-layers/${layer.id}`, { method: "DELETE" });
                            if (activeGisLayerId === layer.id) setActiveGisLayerId(null);
                            refreshGisLayers();
                          }}
                          style={{
                            padding: "4px 6px",
                            borderRadius: 6,
                            border: `1px solid ${PANEL_BORDER}`,
                            background: "rgba(255,255,255,0.02)",
                            color: "rgba(255,255,255,0.3)",
                            cursor: "pointer",
                            fontSize: "0.6rem",
                            lineHeight: 1,
                          }}
                          title="Delete layer"
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setShowWmsImport(true)}
                    style={{
                      padding: "4px 8px",
                      borderRadius: 6,
                      border: "1px solid rgba(255, 152, 0, 0.3)",
                      background: "rgba(255, 152, 0, 0.08)",
                      color: "rgba(255, 152, 0, 0.8)",
                      cursor: "pointer",
                      textAlign: "left",
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      marginTop: 2,
                    }}
                  >
                    + Import from WMS...
                  </button>
                </div>
              )}
              {isActive && step.id === "gis-layers" && !activeTerrain && (
                <div style={{
                  marginLeft: 38,
                  fontSize: "0.65rem",
                  opacity: 0.4,
                  padding: "4px 0",
                }}>
                  Load a terrain model first
                </div>
              )}
            </div>
            );
          })}

          {/* Terrain selector -- removed from here, now inline under step 1 */}
        </div>

        {/* Main content area */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {/* Compact step info bar */}
          {currentStep && (
            <div style={{
              padding: "8px 16px",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}>
              <div style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                background: "rgba(123, 31, 162, 0.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.72rem",
                fontWeight: 700,
                color: "#ce93d8",
                flexShrink: 0,
              }}>
                {currentStep.number}
              </div>
              <div style={{ fontSize: "0.82rem", fontWeight: 600 }}>{currentStep.title}</div>
              <div style={{ fontSize: "0.68rem", opacity: 0.4, flex: 1 }}>{currentStep.description}</div>
              {currentStep.status === "coming-soon" && (
                <span style={{
                  fontSize: "0.6rem",
                  padding: "2px 8px",
                  borderRadius: 4,
                  background: "rgba(255, 152, 0, 0.12)",
                  color: "rgba(255, 152, 0, 0.7)",
                  fontWeight: 600,
                  flexShrink: 0,
                }}>
                  Coming soon
                </span>
              )}
              {activeTerrain && (
                <span style={{
                  fontSize: "0.65rem",
                  padding: "2px 8px",
                  borderRadius: 4,
                  background: "rgba(76, 175, 80, 0.1)",
                  border: "1px solid rgba(76, 175, 80, 0.2)",
                  color: "#81c784",
                  fontWeight: 500,
                  flexShrink: 0,
                }}>
                  {activeTerrain.width}×{activeTerrain.height} • {activeTerrain.stats?.min ?? "?"}–{activeTerrain.stats?.max ?? "?"} m
                </span>
              )}
            </div>
          )}

          {/* 3D viewport */}
          <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
            {activeTerrain ? (
              <Canvas
                gl={{ alpha: false, antialias: true }}
                camera={{ fov: 50, near: 0.1, far: 50000, position: [0, 500, 800] }}
                style={{ width: "100%", height: "100%", background: "#0a1628" }}
              >
                <ambientLight intensity={0.4} />
                <directionalLight position={[200, 500, 300]} intensity={1} />
                <directionalLight position={[-200, 300, -100]} intensity={0.3} />
                {/* Root group rotates from cartesian (X=east, Y=north, Z=up) to Three.js (X=right, Y=up, Z=-forward) */}
                <group rotation={[-Math.PI / 2, 0, 0]}>
                  <TerrainMesh3D
                    terrain={activeTerrain}
                    originOffset={{
                      x: (activeTerrain.extent.minEasting + activeTerrain.extent.maxEasting) / 2,
                      y: (activeTerrain.extent.minNorthing + activeTerrain.extent.maxNorthing) / 2,
                      z: activeTerrain.stats?.mean ?? 0,
                    }}
                    contours={showContours}
                    contourInterval={contourInterval}
                    colorMode={showElevationColors ? "elevation" : "solid"}
                    color="#a09080"
                    hillshade={showHillshade}
                    overlayTexture={gisOverlayTexture}
                  />
                  <SceneOverlays
                    terrain={activeTerrain}
                    originOffset={{
                      x: (activeTerrain.extent.minEasting + activeTerrain.extent.maxEasting) / 2,
                      y: (activeTerrain.extent.minNorthing + activeTerrain.extent.maxNorthing) / 2,
                      z: activeTerrain.stats?.mean ?? 0,
                    }}
                  />
                </group>
                <OrbitControls makeDefault enableDamping dampingFactor={0.1} />
                <OrbitPivotSnap />
                <gridHelper args={[2000, 40, "rgba(255,255,255,0.08)", "rgba(255,255,255,0.04)"]} position={[0, -50, 0]} />
              </Canvas>
            ) : (
              <div style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 12,
                opacity: 0.3,
              }}>
                <div style={{ fontSize: "2rem" }}>{"⛰"}</div>
                <div style={{ fontSize: "0.85rem", fontWeight: 600 }}>No terrain loaded</div>
                <div style={{ fontSize: "0.72rem" }}>Import a terrain model to begin geological modeling</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* WMS Import Modal */}
      {showWmsImport && activeTerrain && (
        <WmsImportModal
          terrain={activeTerrain}
          onClose={() => setShowWmsImport(false)}
          onSaved={() => { refreshGisLayers(); setShowWmsImport(false); }}
        />
      )}
    </div>
  );
}

/* ── WMS layer options ── */
const WMS_LAYER_OPTIONS = [
  {
    value: "Berggrunn_lokal_bergartsenheter,Berggrunn_lokal_linearstrukturer,Berggrunn_lokal_strukturpunkter_linje,Berggrunn_lokal_strukturpunkter_plan,Berggrunn_lokal_strukturpunkter_linje_tekst,Berggrunn_lokal_strukturpunkter_plan_tekst",
    label: "Berggrunn N50",
    wmsService: "BerggrunnWMS3",
    legendPath: "wms/berggrunn/BerggrunnWMS3/lokal/bergartsenheter_linearstrukturer_strukturmalinger_w200px.png",
  },
  {
    value: "Losmasser_sammenstilt_lokal_losmassetyper,Losmasser_sammenstilt_lokal_losmassegrenser,Losmasser_sammenstilt_lokal_hovedlandformlinjer,Losmasser_sammenstilt_lokal_landformlinjer,Losmasser_sammenstilt_detalj_lokal_punkter,Losmasser_fullzoom_lokal_losmassetyper_tekst",
    label: "Løsmasse N50",
    wmsService: "LosmasserWMS3",
    legendPath: "wms/losmasse/LosmasserWMS3/lokal/losmassetyper_grenser_formlinjer_punkter_w200px.png",
  },
];

/* ── WMS Import Modal ── */
function WmsImportModal({ terrain, onClose, onSaved }: {
  terrain: TerrainData;
  onClose: () => void;
  onSaved: () => void;
}) {
  const mapRef = useRef<HTMLDivElement>(null);
  const olMapRef = useRef<OlMap | null>(null);
  const wmsLayerRef = useRef<ImageLayer | null>(null);
  const [selectedOptionIdx, setSelectedOptionIdx] = useState(0);
  const selectedOption = WMS_LAYER_OPTIONS[selectedOptionIdx];
  const [saving, setSaving] = useState(false);
  const [label, setLabel] = useState("");
  const [wmsOpacity, setWmsOpacity] = useState(0.7);

  // Initialize OpenLayers map
  useEffect(() => {
    if (!mapRef.current || olMapRef.current) return;

    const ext = terrain.extent;
    // proj4 defs already registered by mapUtils import
    const sw = proj4("EPSG:25832", "EPSG:3857", [ext.minEasting, ext.minNorthing]);
    const ne = proj4("EPSG:25832", "EPSG:3857", [ext.maxEasting, ext.maxNorthing]);
    const extentMerc: [number, number, number, number] = [sw[0], sw[1], ne[0], ne[1]];

    // Terrain extent box
    const boxFeature = new Feature({
      geometry: new Polygon([[
        [sw[0], sw[1]], [ne[0], sw[1]], [ne[0], ne[1]], [sw[0], ne[1]], [sw[0], sw[1]],
      ]]),
    });
    const boxLayer = new VectorLayer({
      source: new VectorSource({ features: [boxFeature] }),
      style: new Style({
        stroke: new Stroke({ color: "rgba(255, 152, 0, 0.9)", width: 2.5, lineDash: [8, 4] }),
        fill: new Fill({ color: "rgba(255, 152, 0, 0.06)" }),
      }),
    });

    // NGU WMS layer via proxy
    const wmsLayer = new ImageLayer({
      source: new ImageWMS({
        url: `/api/geo-map/wms-proxy/${selectedOption.wmsService}`,
        params: {
          LAYERS: selectedOption.value,
          FORMAT: "image/png",
          TRANSPARENT: "TRUE",
        },
        ratio: 1,
        serverType: "mapserver",
      }),
      visible: true,
    });
    wmsLayerRef.current = wmsLayer;

    const baseLayers = createBaseLayers();
    const map = new OlMap({
      target: mapRef.current!,
      layers: [...baseLayers, wmsLayer, boxLayer],
      view: new View({
        center: [(sw[0] + ne[0]) / 2, (sw[1] + ne[1]) / 2],
        zoom: 13,
      }),
    });

    // Fit to terrain extent with padding
    map.getView().fit(extentMerc, { padding: [60, 60, 60, 60], maxZoom: 16 });

    olMapRef.current = map;

    return () => {
      olMapRef.current?.setTarget(undefined);
      olMapRef.current = null;
    };
  }, [terrain]);

  // Update WMS layer when selection changes
  useEffect(() => {
    if (!wmsLayerRef.current) return;
    const src = wmsLayerRef.current.getSource();
    if (src) {
      (src as ImageWMS).updateParams({ LAYERS: selectedOption.value });
      (src as ImageWMS).setUrl(`/api/geo-map/wms-proxy/${selectedOption.wmsService}`);
    }
  }, [selectedOption]);

  // Update WMS layer opacity
  useEffect(() => {
    if (wmsLayerRef.current) wmsLayerRef.current.setOpacity(wmsOpacity);
  }, [wmsOpacity]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const ext = terrain.extent;
      const bbox = `${ext.minEasting},${ext.minNorthing},${ext.maxEasting},${ext.maxNorthing}`;
      // Request high-res image from WMS for the terrain extent
      const terrainW = ext.maxEasting - ext.minEasting;
      const terrainH = ext.maxNorthing - ext.minNorthing;
      const texSize = 2048;
      const w = terrainW >= terrainH ? texSize : Math.round(texSize * terrainW / terrainH);
      const h = terrainH >= terrainW ? texSize : Math.round(texSize * terrainH / terrainW);

      const wmsResp = await fetch(`/api/geo-map/wms?bbox=${bbox}&width=${w}&height=${h}&layer=${selectedOption.value}&service=${selectedOption.wmsService}`);
      if (!wmsResp.ok) throw new Error(`WMS error: ${wmsResp.status}`);
      const blob = await wmsResp.blob();

      // Convert to data URL
      const reader = new FileReader();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      const layerLabel = label.trim() || selectedOption.label;

      const saveResp = await fetch("/api/geo-layers/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          label: layerLabel,
          wmsLayers: selectedOption.value,
          bbox,
          width: w,
          height: h,
          imageDataUrl: dataUrl,
        }),
      });
      if (!saveResp.ok) throw new Error("Save failed");
      onSaved();
    } catch (err) {
      console.error("Failed to save WMS layer:", err);
      alert("Failed to save layer. Check console for details.");
    } finally {
      setSaving(false);
    }
  }, [terrain, selectedOption, label, onSaved]);

  return (
    <div style={{
      position: "fixed",
      inset: 0,
      zIndex: 1000,
      background: "rgba(0,0,0,0.7)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}>
      <div style={{
        width: "min(90vw, 1100px)",
        height: "min(85vh, 800px)",
        background: "#0f1d32",
        borderRadius: 12,
        border: "1px solid rgba(255,255,255,0.12)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{
          padding: "12px 16px",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexShrink: 0,
        }}>
          <div style={{ flex: 1, fontWeight: 700, fontSize: "0.9rem" }}>Import from WMS</div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "rgba(255,255,255,0.5)",
              cursor: "pointer",
              fontSize: "1.2rem",
              padding: "4px 8px",
            }}
          >
            ✕
          </button>
        </div>

        {/* Controls bar */}
        <div style={{
          padding: "10px 16px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexShrink: 0,
          flexWrap: "wrap",
        }}>
          <label style={{ fontSize: "0.72rem", fontWeight: 600, opacity: 0.7 }}>WMS Layer:</label>
          <select
            value={selectedOptionIdx}
            onChange={(e) => setSelectedOptionIdx(Number(e.target.value))}
            style={{
              background: "rgba(255,255,255,0.08)",
              border: `1px solid ${PANEL_BORDER}`,
              borderRadius: 6,
              color: "#f7f8fb",
              fontSize: "0.72rem",
              padding: "4px 8px",
              cursor: "pointer",
            }}
          >
            {WMS_LAYER_OPTIONS.map((opt, idx) => (
              <option key={opt.value} value={idx}>{opt.label}</option>
            ))}
          </select>

          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.72rem", fontWeight: 600, opacity: 0.7 }}>
            Opacity:
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={wmsOpacity}
              onChange={(e) => setWmsOpacity(Number(e.target.value))}
              style={{ width: 80, accentColor: "#7b1fa2", cursor: "pointer" }}
            />
            <span style={{ fontSize: "0.65rem", opacity: 0.5, fontFamily: "monospace", minWidth: 28 }}>
              {Math.round(wmsOpacity * 100)}%
            </span>
          </label>

          <div style={{ flex: 1 }} />

          <input
            type="text"
            placeholder="Layer name (optional)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            style={{
              background: "rgba(255,255,255,0.06)",
              border: `1px solid ${PANEL_BORDER}`,
              borderRadius: 6,
              color: "#f7f8fb",
              fontSize: "0.72rem",
              padding: "4px 10px",
              width: 180,
            }}
          />

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: "6px 16px",
              borderRadius: 6,
              border: "1px solid rgba(76, 175, 80, 0.5)",
              background: saving ? "rgba(76, 175, 80, 0.1)" : "rgba(76, 175, 80, 0.2)",
              color: "#81c784",
              cursor: saving ? "wait" : "pointer",
              fontSize: "0.75rem",
              fontWeight: 700,
            }}
          >
            {saving ? "Saving..." : "Save as image"}
          </button>
        </div>

        {/* Map + Legend */}
        <div style={{ flex: 1, minHeight: 0, position: "relative", display: "flex" }}>
          <div ref={mapRef} style={{ flex: 1, minWidth: 0 }} />

          {/* WMS Legend panel */}
          <div style={{
            width: 280,
            flexShrink: 0,
            borderLeft: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(0,0,0,0.4)",
            overflowY: "auto",
            overflowX: "auto",
            padding: "10px",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}>
            <div style={{ fontSize: "0.68rem", fontWeight: 600, opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Legend
            </div>
            <img
              src={`/api/geo-map/legend-image?path=${selectedOption.legendPath}`}
              alt={`Legend for ${selectedOption.label}`}
              style={{ width: "100%", borderRadius: 4, background: "rgba(255,255,255,0.95)" }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
          </div>

          {/* Extent indicator */}
          <div style={{
            position: "absolute",
            bottom: 12,
            left: 12,
            background: "rgba(0,0,0,0.7)",
            borderRadius: 6,
            padding: "6px 10px",
            fontSize: "0.65rem",
            color: "rgba(255,255,255,0.6)",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}>
            <div style={{ width: 16, height: 12, border: "2px dashed rgba(255, 152, 0, 0.9)", borderRadius: 2 }} />
            Terrain extent — image will be saved for this area
          </div>
        </div>
      </div>
    </div>
  );
}
